import {
  NgTemplateOutlet,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
} from '@angular/core';
import type {
  Traveler,
} from '@dapi/sdk';
import {
  O3rComponent,
} from '@o3r/core';
import {
  RequestParametersService,
} from '@o3r/dynamic-content';
import {
  PriceInformationSkeletonPresComponent,
  TimetableSkeletonPresComponent,
  UpsellPremiumSkeletonPresComponent,
} from '@refx/booking-components';
import {
  RequestParams,
} from '@refx/shared-common';
import {
  BasicInFlowLayoutModule,
  BasicLayoutModule,
  CalendarSkeletonPresComponent,
  FlightConfirmationSkeletonPresComponent,
  FlightRecapSkeletonPresComponent,
  IdentificationSkeletonPresComponent,
  ItineraryCardSkeletonPresComponent,
  MessagesPanelSkeletonPresComponent,
  PageTitleSkeletonPresComponent,
  PassengerRecapSkeletonPresComponent,
  ServiceCatalogSkeletonPresComponent,
} from '@refx/shared-components';
import {
  NgxSkeletonLoaderModule,
} from 'ngx-skeleton-loader';
import {
  AppRoutes,
  BookingRoutes,
  ManageBookingRoutes,
  skeletalRoutes,
} from '../../../configuration';

@O3rComponent({
  componentType: 'Component'
})
@Component({
  selector: 'refx-app-skeleton-pres',
  styleUrls: ['./app-skeleton-pres.style.scss'],
  templateUrl: './app-skeleton-pres.template.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgTemplateOutlet,
    CalendarSkeletonPresComponent,
    UpsellPremiumSkeletonPresComponent,
    BasicInFlowLayoutModule,
    BasicLayoutModule,
    FlightConfirmationSkeletonPresComponent,
    IdentificationSkeletonPresComponent,
    ItineraryCardSkeletonPresComponent,
    MessagesPanelSkeletonPresComponent,
    NgxSkeletonLoaderModule,
    PageTitleSkeletonPresComponent,
    PassengerRecapSkeletonPresComponent,
    FlightRecapSkeletonPresComponent,
    ServiceCatalogSkeletonPresComponent,
    PriceInformationSkeletonPresComponent,
    TimetableSkeletonPresComponent
  ]
})
export class AppSkeletonPresComponent {
  /**
   * The route for which the skeleton must be displayed
   */
  public skeletonRoute: AppRoutes = BookingRoutes.upsell;

  /**
   * The data type for which determines the layout of the skeleton
   */
  public selectedDataType: 'Cart' | 'Order' | null;

  /**
   * The list of traveler types in the selected data type
   */
  public travelers: string[] = ['ADT'];

  /**
   * The number of bounds in the selected data
   */
  public nbBounds = 2;

  /**
   * The services to display in the skeleton
   */
  public services: any[] = [
    {
      displayExtraDetails: true
    },
    {
      displayExtraDetails: false
    },
    {
      displayExtraDetails: false
    },
    {
      displayExtraDetails: false
    },
    {
      displayExtraDetails: false
    },
    {
      displayExtraDetails: false
    }
  ];

  constructor(
    protected requestParametersService: RequestParametersService
  ) {
    this.constructTemplateData();
    if (!this.selectedDataType) {
      this.determineSelectedDataFromDeeplink();
    }
    this.determineSkeletonPage();
  }

  /**
   * Determines the page type to display the skeleton for
   */
  protected determineSkeletonPage() {
    const route = window.location.pathname;
    this.skeletonRoute = skeletalRoutes.find((skeletalRoute) => route.includes(skeletalRoute)) || BookingRoutes.upsell;
    this.skeletonRoute = this.skeletonRoute === ManageBookingRoutes.retrieve ? (this.selectedDataType ? ManageBookingRoutes.confirmation : ManageBookingRoutes.retrieve) : this.skeletonRoute;
  }

  /**
   * Constructs the template data for the skeleton from order
   * It makes use of the session storage to fetch the data instead
   * of the store because the store is not populated yet and also
   * to avoid bloating the main bundle size with references to the
   * Cart and Order models and their dependencies since this skeleton
   * is part of the main bundle
   */
  protected constructTemplateData() {
    try {
      const rawOrder = sessionStorage.getItem('order');
      if (rawOrder) {
        const order = JSON.parse(rawOrder);
        const currentOrder = order?.entities?.[order?.ids?.[0]];
        if (currentOrder) {
          this.selectedDataType = 'Order';
          this.travelers = currentOrder.travelers?.map((traveler: Traveler) => traveler.passengerTypeCode) || ['ADT'];
          this.nbBounds = currentOrder.air.bounds.length || 1;
        } else {
          this.constructTemplateDataFromCart();
        }
      }
    } catch {
      this.constructTemplateDataFromCart();
    }
  }

  /**
   * Constructs the template data for the skeleton from cart
   */
  protected constructTemplateDataFromCart() {
    try {
      const rawCart = sessionStorage.getItem('cart');
      if (rawCart) {
        const cart = JSON.parse(rawCart);
        const currentCart = cart?.entities?.[cart?.ids?.[0]];
        if (currentCart) {
          this.selectedDataType = 'Cart';
          this.travelers = currentCart.travelers?.map((traveler: Traveler) => traveler.passengerTypeCode) || ['ADT'];
          this.nbBounds = currentCart.airOffers?.[0]?.offerItems?.[0]?.air?.bounds?.length || 1;
        }
      }
    } catch {}
  }

  /**
   * Determines the selected data for the skeleton from the deeplink parameters
   */
  protected determineSelectedDataFromDeeplink() {
    const cartId = this.requestParametersService.getParameter(RequestParams.cartId);
    const orderId = this.requestParametersService.getPostParameter(RequestParams.orderId) || this.requestParametersService.getPostParameter(RequestParams.recLoc);
    this.selectedDataType = orderId ? 'Order' : (cartId ? 'Cart' : null);
  }
}
